import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, withKeys as _withKeys, createStaticVNode as _createStaticVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2dca7d0c"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["onKeydown"]
const _hoisted_2 = { class: "location-picker__header" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "location-picker__title" }, "סמן שכונה על המפה", -1))
const _hoisted_4 = { class: "location-picker__actions" }
const _hoisted_5 = ["aria-label"]
const _hoisted_6 = { class: "location-picker__map-content" }
const _hoisted_7 = { class: "tw-form-control tw-form-control__with-icon--left location-picker__search" }
const _hoisted_8 = /*#__PURE__*/_createStaticVNode("<svg class=\"tw-form-control__icon--left\" width=\"32\" height=\"28\" viewBox=\"0 0 32 28\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\" data-v-2dca7d0c><g clip-path=\"url(#clip0_1247_141)\" data-v-2dca7d0c><path d=\"M25.8099 9.76988C26.3499 11.4099 26.5599 13.1799 26.3699 14.9999C25.6599 21.9899 19.4199 27.0799 12.4399 26.3699C5.4599 25.6599 0.359905 19.4199 1.0699 12.4299C1.7799 5.43988 8.0099 0.359883 14.9999 1.06988C18.0299 1.37988 20.7099 2.72988 22.7099 4.72988\" stroke=\"#2C267A\" stroke-width=\"2\" stroke-miterlimit=\"10\" stroke-linecap=\"round\" data-v-2dca7d0c></path><path d=\"M25.1499 20.3999L30.8199 23.8099\" stroke=\"#2C267A\" stroke-width=\"2\" stroke-miterlimit=\"10\" stroke-linecap=\"round\" data-v-2dca7d0c></path></g><defs data-v-2dca7d0c><clipPath id=\"clip0_1247_141\" data-v-2dca7d0c><rect width=\"31.82\" height=\"27.43\" fill=\"white\" data-v-2dca7d0c></rect></clipPath></defs></svg>", 1)

import { computed, onMounted, ref, watch } from 'vue'
import { GoogleMap, Marker } from 'vue3-google-map'

import { useDialog } from '@/stores/dialogStore';
import { PickedLocation } from '@/models/AdditionalDataTypes';
import CloseIcon from '@/components/Icons/CloseIcon.vue';
import { AddressService } from '@/services/address.service';
import { useInterviewStore } from '@/stores/interviewStore';

export interface LocationPickerDataType {
    zoom: number,
    // https://console.cloud.google.com/google/maps-apis/studio/maps
    // for styling
    mapId: string,
    //
    // https://developers.google.com/maps/get-started#procedures
    googleApiKey?: string,
    askCurrentGeo?: boolean,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'LocationPicker',
  setup(__props) {

const interviewStore = useInterviewStore()

const addressRef = ref<HTMLInputElement | undefined>(undefined);
const mapRef = ref(undefined);
const autocomplete = ref<google.maps.places.Autocomplete | undefined>(undefined);

const dialogStore = useDialog();
const data = dialogStore.dialogContentData as LocationPickerDataType;

const location = ref<string | undefined>(undefined);
const defaultCenter = ref({ lat: 32.08849714422459, lng: 34.7819652135678 });

const markerCenter = ref<{lat: number, lng: number} | undefined>(undefined);

const modalClose = computed(() => interviewStore.getLocalization('Accessability.Modal.Close') ?? "")

watch(location, () => {
    if (location.value !== addressRef.value!.value) {
        addressRef.value!.value = location.value || '';
    }
})

const markerOptions = computed(() => ({
    position: markerCenter.value,
    label: location.value ? location.value[0] : '',
    title: location.value ? location.value : '',
}));

const culture = computed(() => document.documentElement.dataset.culture?.toString() ?? '')
const gmapKey = computed(() => interviewStore.googleMapKey)

async function setLocation(lat: number, lng: number) {
    markerCenter.value = { lat, lng };
    const address = await AddressService.getAddress([lat, lng], gmapKey.value);

    if (address) {
        location.value = onLocationPicked(address);
    }
}

function submit(): void {
    if (location.value) {
        dialogStore.close(location.value);
    }
}

function close(): void {
    dialogStore.close();
}

function onLocationPicked(ans: PickedLocation): string {
    if (ans.street) {
        return `${ans.country}; ${ans.city}; ${ans.street} ${ans.streetNumber}`;
    }

    if (ans.city) {
        return `${ans.country}; ${ans.city}`;
    }

    return ans.country;
}

function onLocationPickedFromDropdown(addressComponents: google.maps.GeocoderAddressComponent[]): void {
    if (!addressComponents) {
        return;
    }

    const country = addressComponents.find(adr => adr.types.includes('country'))?.long_name ?? '';
    const city = addressComponents.find(adr => adr.types.includes('locality'))?.long_name ?? '';
    const street = addressComponents.find(adr => adr.types.includes('route'))?.long_name ?? '';
    const streetNumber = addressComponents.find(adr => adr.types.includes('street_number'))?.long_name ?? '';

    location.value = onLocationPicked({ country, city, street, streetNumber })

    moveFocusToSubmitButton()
}

async function selectMarker(e: google.maps.MapMouseEvent) {
    try {
        await setLocation(e.latLng!.lat(), e.latLng!.lng())
    } catch (error) {
        console.warn('Error during location pick', e)
    }
}

function setupGooglePlaces() {
    autocomplete.value = new google.maps.places.Autocomplete(addressRef.value!, {
        types: ['address'],
        fields: ['address_components', 'geometry'],
        bounds: new google.maps.LatLngBounds(),
    });

    google.maps.event.addListener(autocomplete.value, 'place_changed', async () => {
        const place = autocomplete.value!.getPlace();
        const lat = place?.geometry?.location?.lat();
        const lng = place?.geometry?.location?.lng();

        if (!place || !lat || !lng) {
            return;
        }

        onLocationPickedFromDropdown(place.address_components!);
        defaultCenter.value = { lat, lng };
        markerCenter.value = defaultCenter.value;
    });
}

function trapFocus(event: KeyboardEvent) {
    const container = document.querySelector('.location-picker__map-content') as HTMLElement
    const closeButton = document.querySelector('.location-picker__close') as HTMLElement
    const interactiveElements = container.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])') as NodeListOf<HTMLElement>
    const lastInteractiveElement = interactiveElements[interactiveElements.length - 1]

    if (document.activeElement === lastInteractiveElement && !event.shiftKey && event.key === 'Tab') {
        event.preventDefault();
        closeButton.focus()
    } else if (document.activeElement === closeButton && event.shiftKey && event.key === 'Tab') {
        event.preventDefault();
        lastInteractiveElement.focus();
    }
}

function moveFocusToSubmitButton() {
    const submitButton = document.querySelector('.location-picker__select') as HTMLElement
    submitButton?.focus();
}

watch(
    () => (mapRef.value as any)?.ready,
    ready => ready && !autocomplete.value && setupGooglePlaces(),
    { immediate: true },
)

onMounted(() => {
    if (!data.askCurrentGeo) {
        return;
    }

    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
        await setLocation(coords.latitude, coords.latitude)
    })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "location-picker",
    onKeydown: _withKeys(trapFocus, ["tab"])
  }, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("button", {
          onClick: close,
          class: "location-picker__close",
          "aria-label": _unref(modalClose)
        }, [
          _createVNode(CloseIcon)
        ], 8, _hoisted_5),
        _createVNode(_Transition, {
          name: "tw-fade-top",
          onAfterEnter: moveFocusToSubmitButton
        }, {
          default: _withCtx(() => [
            (!!_unref(markerOptions).position)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  onClick: submit,
                  class: "tw-btn tw-btn__flat tw-btn__flat--yellow location-picker__select"
                }, " אישור "))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("input", {
          ref_key: "addressRef",
          ref: addressRef,
          type: "text",
          placeholder: "חיפוש על המפה",
          class: "tw-form-control__input"
        }, null, 512),
        _hoisted_8
      ]),
      _createVNode(_unref(GoogleMap), {
        ref_key: "mapRef",
        ref: mapRef,
        "fullscreen-control": false,
        "street-view-control": false,
        "map-type-control": false,
        "map-id": _unref(data).mapId,
        "api-key": _unref(gmapKey),
        language: _unref(culture),
        class: "location-picker__map",
        center: markerCenter.value ?? defaultCenter.value,
        zoom: _unref(data).zoom,
        onClick: selectMarker
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Marker), { options: _unref(markerOptions) }, null, 8, ["options"])
        ]),
        _: 1
      }, 8, ["map-id", "api-key", "language", "center", "zoom"])
    ])
  ], 40, _hoisted_1))
}
}

})